import { FC } from 'react';
import { Box } from '@mui/material';
import { LoadingSection } from 'components';
import { WATERFALL_OPTIONS } from 'dashboard409a/utilities';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { getColorFromPalette } from 'dashboard409a/utilities/getColor';
import { BreakpointChartProps, IWaterfallData, ParsedDataType } from './types';
import { LineBarChart } from '../../../LineBarChart';

// Function to parse the original data into a format suitable for the chart
const getParsedData = (originalData: IWaterfallData) => {
  const { breakpoints } = originalData.waterfall_breakpoints || {};
  if (!breakpoints) {
    return [];
  }

  return breakpoints.reduce((acc: ParsedDataType, breakpoint) => {
    breakpoint.series.forEach(serie => {
      let entry = acc.find(el => el.security === serie.security);
      if (!entry) {
        entry = { security: serie.security, breakpoints: [{ breakpoint: '', value: 0 }] };
        acc.push(entry);
      }
      entry.breakpoints.push({
        breakpoint: breakpoint.name,
        value: typeof serie.value === 'string' ? parseFloat(serie.value) : serie.value ?? 0,
      });
    });
    return acc;
  }, []);
};

// Function to get the labels for the x-axis
const getXLabels = (data: IWaterfallData): [string, number][] => {
  const labels: [string, number][] = data.waterfall_breakpoints.breakpoints.map(
    ({ name, total }) => [name, typeof total === 'string' ? parseFloat(total) : total] as [string, number]
  );

  if (labels.length > 0 && labels[0][1] !== 0) {
    labels.unshift(['$0', 0]);
  }

  if (labels[labels.length - 1][1] === Infinity) {
    labels.pop();
  }

  return labels.filter(([, total]) => total != null && !Number.isNaN(total));
};

const BreakpointChart: FC<BreakpointChartProps> = ({ data, isLoading }) => {
  if (isLoading || !data) {
    return <LoadingSection />;
  }

  const xLabels = getXLabels(data);
  const xLabelsWithFormattedCurrency = xLabels.map(label => `${label[0]} (${formatCurrency({ value: label[1] })})`);
  const parsedData = getParsedData(data);

  // Prepare the data for the chart
  const dataChart = {
    labels: xLabelsWithFormattedCurrency,
    datasets: parsedData.map((item, parsedDataIndex) => ({
      type: 'line' as const,
      label: item.security,
      data: item.breakpoints.map((info, index) => ({
        x: xLabels[index]?.[1] ?? 0,
        y: typeof info.value === 'string' ? parseFloat(info.value) : info.value ?? 0,
      })),
      backgroundColor: getColorFromPalette(parsedDataIndex),
      pointRadius: 5,
      fill: true,
      showLine: true,
    })),
  };

  return (
    <Box sx={{ position: 'relative', height: 400 }}>
      <LineBarChart data={dataChart} options={WATERFALL_OPTIONS(xLabelsWithFormattedCurrency)} />
    </Box>
  );
};

export default BreakpointChart;
